// Thin client for the admin-only /admin/role-settings + /admin/suppressions
// endpoints (PR #72 backend scaffold).
//
// Kept separate from the larger adminService so this surface stays tightly
// scoped to the AdminSettingsDrawer UI consumer. Future Send-List wiring
// (PR 3 of the Admin Settings rollout) will read role_settings via this same
// client to gate toggle visibility + apply role defaults.

import { api } from "../lib/api";

export type AdminSettingsRole = "admin" | "agent";

export type AdminSettingsSendMode = "personal" | "ultra_marketing";

/** Shape of a single role_settings row from /admin/role-settings */
export interface RoleSettingsRow {
  role: AdminSettingsRole;
  send_mode_allowed_toggle: boolean;
  send_mode_default: AdminSettingsSendMode;
  include_property_images_allowed_toggle: boolean;
  include_property_images_default: boolean;
  include_floor_plan_images_allowed_toggle: boolean;
  include_floor_plan_images_default: boolean;
  link_floor_plans_to_ln_page_allowed_toggle: boolean;
  link_floor_plans_to_ln_page_default: boolean;
  property_opaque_overlay_allowed_toggle: boolean;
  property_opaque_overlay_default: boolean;
  dark_mode_only_allowed_toggle: boolean;
  dark_mode_only_default: boolean;
  updated_at: string;
  updated_by: string | null;
}

/** Partial-update payload for PUT /admin/role-settings/{role} */
export type RoleSettingsPatch = Partial<
  Pick<
    RoleSettingsRow,
    | "send_mode_allowed_toggle"
    | "send_mode_default"
    | "include_property_images_allowed_toggle"
    | "include_property_images_default"
    | "include_floor_plan_images_allowed_toggle"
    | "include_floor_plan_images_default"
    | "link_floor_plans_to_ln_page_allowed_toggle"
    | "link_floor_plans_to_ln_page_default"
    | "property_opaque_overlay_allowed_toggle"
    | "property_opaque_overlay_default"
    | "dark_mode_only_allowed_toggle"
    | "dark_mode_only_default"
  >
>;

export interface SuppressionRow {
  id: string;
  recipient_email: string;
  reason: string | null;
  source_tracking_id: string | null;
  unsubscribed_at: string;
}

export interface EmailPreferenceRow {
  id: string;
  recipient_email: string;
  direct_email_enabled: boolean;
  marketing_email_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export type AssignableRole = "admin" | "manager" | "agent";

export interface AdminUserRow {
  id: string;
  email: string;
  role: AssignableRole | "demo";
  access_scope: "production" | "sandbox" | string;
  full_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface RoleChangeRow {
  id: string;
  changed_by: string | null;
  target_user: string;
  from_role: string;
  to_role: string;
  reason: string | null;
  changed_at: string;
}

interface ListRoleSettingsResponse {
  role_settings: RoleSettingsRow[];
}

interface UpdateRoleSettingsResponse {
  role_settings: RoleSettingsRow;
}

interface ListSuppressionsResponse {
  suppressions: SuppressionRow[];
  limit: number;
  offset: number;
}

interface ListEmailPreferencesResponse {
  preferences: EmailPreferenceRow[];
  limit: number;
  offset: number;
}

export async function listRoleSettings(): Promise<RoleSettingsRow[]> {
  const body = await api.get<ListRoleSettingsResponse>("/admin/role-settings");
  return body.role_settings ?? [];
}

export async function updateRoleSettings(
  role: AdminSettingsRole,
  patch: RoleSettingsPatch
): Promise<RoleSettingsRow> {
  const body = await api.put<UpdateRoleSettingsResponse>(
    `/admin/role-settings/${role}`,
    patch
  );
  return body.role_settings;
}

export async function listSuppressions(
  limit = 100,
  offset = 0
): Promise<ListSuppressionsResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return api.get<ListSuppressionsResponse>(
    `/admin/suppressions?${params.toString()}`
  );
}

export async function listEmailPreferences(
  limit = 100,
  offset = 0
): Promise<ListEmailPreferencesResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return api.get<ListEmailPreferencesResponse>(
    `/admin/email-preferences?${params.toString()}`
  );
}

export async function listAdminUsers(): Promise<AdminUserRow[]> {
  const body = await api.get<{ users: AdminUserRow[] }>("/admin/users");
  return body.users ?? [];
}

export interface CreateUserPayload {
  email: string;
  password: string;
  role: AssignableRole;
  full_name?: string;
}

export async function createAdminUser(
  payload: CreateUserPayload
): Promise<{ user_id: string; email: string; role: AssignableRole }> {
  return api.post<{ user_id: string; email: string; role: AssignableRole }>(
    "/admin/users",
    payload
  );
}

export async function updateUserRole(
  userId: string,
  role: AssignableRole,
  reason?: string
): Promise<{ user: AdminUserRow; changed: boolean }> {
  return api.put<{ user: AdminUserRow; changed: boolean }>(
    `/admin/users/${userId}/role`,
    reason ? { role, reason } : { role }
  );
}

export async function listRoleChanges(
  limit = 50,
  offset = 0
): Promise<{ role_changes: RoleChangeRow[]; limit: number; offset: number }> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return api.get<{
    role_changes: RoleChangeRow[];
    limit: number;
    offset: number;
  }>(`/admin/role-changes?${params.toString()}`);
}

/** Locked, ordered list of settings shown in the Role Settings tab. Each
 *  entry maps a Send-List feature to its `_allowed_toggle` + `_default`
 *  field names on the role_settings row. Centralizing here keeps the UI
 *  in lockstep with backend column names + future schema additions. */
export interface RoleSettingsFieldSpec {
  key: string;
  label: string;
  description?: string;
  allowedField: keyof RoleSettingsRow;
  defaultField: keyof RoleSettingsRow;
  /** "boolean" → render as Switch · "send_mode" → render as Select */
  defaultControl: "boolean" | "send_mode";
}

export const ROLE_SETTINGS_FIELDS: RoleSettingsFieldSpec[] = [
  {
    key: "send_mode",
    label: "Send mode",
    description:
      "Personal lands in Gmail Primary tab; Ultra Marketing lands in Promotional.",
    allowedField: "send_mode_allowed_toggle",
    defaultField: "send_mode_default",
    defaultControl: "send_mode",
  },
  {
    key: "include_property_images",
    label: "Include property images",
    description: "Property hero images in the email body.",
    allowedField: "include_property_images_allowed_toggle",
    defaultField: "include_property_images_default",
    defaultControl: "boolean",
  },
  {
    key: "include_floor_plan_images",
    label: "Include floor plan images",
    description: "Floor plan thumbnails alongside listing details.",
    allowedField: "include_floor_plan_images_allowed_toggle",
    defaultField: "include_floor_plan_images_default",
    defaultControl: "boolean",
  },
  {
    key: "link_floor_plans_to_ln_page",
    label: "Link floor plans to LocateNow page",
    description: "Beta — live availability page on locatenowatl.com.",
    allowedField: "link_floor_plans_to_ln_page_allowed_toggle",
    defaultField: "link_floor_plans_to_ln_page_default",
    defaultControl: "boolean",
  },
  {
    key: "property_opaque_overlay",
    label: "Property opaque overlay",
    description: "Darken hero image with overlay for legibility.",
    allowedField: "property_opaque_overlay_allowed_toggle",
    defaultField: "property_opaque_overlay_default",
    defaultControl: "boolean",
  },
  {
    key: "dark_mode_only",
    label: "Dark mode only",
    description: "Force dark-themed email template.",
    allowedField: "dark_mode_only_allowed_toggle",
    defaultField: "dark_mode_only_default",
    defaultControl: "boolean",
  },
];
